import { sanitizePublishedPilotEvent } from "./pilotEventSchema";

const frozenResult = (status, reason) => Object.freeze(
  reason ? { status, reason } : { status }
);
const INVALID_EVENT_RESULT = frozenResult("dropped", "invalid-event");
const QUEUE_FULL_RESULT = frozenResult("dropped", "queue-full");
const URGENT_PUBLISH_RESULT = frozenResult("published");
const URGENT_REJECTED_RESULT = frozenResult("dropped", "urgent-transport");

const requirePositiveInteger = (value, key) => {
  if (!Number.isInteger(value) || value < 1) {
    throw new TypeError(`${key} must be a positive integer`);
  }
};

const requireNonNegativeInteger = (value, key) => {
  if (!Number.isInteger(value) || value < 0) {
    throw new TypeError(`${key} must be a positive integer or zero`);
  }
};

/**
 * Deterministic telemetry adapter for interface tests and local inspection.
 * Only `append` belongs to the pilot seam; `read` and `clear` are adapter
 * conveniences and are intentionally not required by createPilotControl.
 */
export const createMemoryPilotTelemetry = () => {
  const events = [];

  return Object.freeze({
    append(event) {
      events.push(event);
    },
    read() {
      return Object.freeze([...events]);
    },
    clear() {
      events.length = 0;
    },
  });
};

/**
 * Minimal direct adapter used by focused tests and already-buffered callers.
 * Browser production wiring uses createQueuedPublishingPilotTelemetry below.
 */
export const createPublishingPilotTelemetry = (publish) => {
  if (typeof publish !== "function") {
    throw new TypeError("publish must be a function");
  }

  return Object.freeze({
    append(event) {
      return publish(event);
    },
  });
};

/**
 * Explicit adapter for deployments where pilot telemetry is not configured.
 * This is the default browser behaviour: no network or analytics call is made.
 */
export const createDisabledPilotTelemetry = (reason = "disabled") => {
  if (typeof reason !== "string" || reason.trim() === "") {
    throw new TypeError("reason must be a non-empty string");
  }
  const result = frozenResult("dropped", reason.trim());

  return Object.freeze({
    append() {
      return result;
    },
  });
};

/**
 * Bounded, non-blocking production adapter at the telemetry seam.
 *
 * `append` only validates and queues an event. The injected transport receives
 * frozen batches later and can therefore be backed by an owned endpoint or an
 * already-configured analytics publisher without coupling either to product
 * navigation. Failed batches are retried a finite number of times and then
 * dropped; transport availability can never delay the caller.
 */
export const createQueuedPublishingPilotTelemetry = ({
  batchSize = 10,
  enabled = false,
  flushIntervalMs = 1000,
  maxQueueSize = 100,
  maxRetries = 2,
  publishBatch,
  publishUrgent,
  retryDelayMs = 500,
  schedule = setTimeout,
  shouldPublishUrgently = () => false,
  cancel = clearTimeout,
} = {}) => {
  requirePositiveInteger(batchSize, "batchSize");
  requirePositiveInteger(maxQueueSize, "maxQueueSize");
  requireNonNegativeInteger(flushIntervalMs, "flushIntervalMs");
  requireNonNegativeInteger(maxRetries, "maxRetries");
  requireNonNegativeInteger(retryDelayMs, "retryDelayMs");
  if (typeof schedule !== "function") throw new TypeError("schedule must be a function");
  if (typeof cancel !== "function") throw new TypeError("cancel must be a function");
  if (typeof shouldPublishUrgently !== "function") {
    throw new TypeError("shouldPublishUrgently must be a function");
  }

  if (enabled !== true) return createDisabledPilotTelemetry();
  if (typeof publishBatch !== "function") {
    throw new TypeError("publishBatch must be a function when telemetry is enabled");
  }
  if (publishUrgent !== undefined && typeof publishUrgent !== "function") {
    throw new TypeError("publishUrgent must be a function when provided");
  }

  const queue = [];
  let activeBatch = null;
  let scheduled = null;
  let scheduledDelay = null;

  const bufferedCount = () => queue.length + (activeBatch?.events.length || 0);

  const scheduleDrain = (delayMs, callback) => {
    if (scheduled !== null && scheduledDelay <= delayMs) return;
    if (scheduled !== null) cancel(scheduled);
    scheduledDelay = delayMs;
    scheduled = schedule(() => {
      scheduled = null;
      scheduledDelay = null;
      callback();
    }, delayMs);
  };

  const scheduleNextBatch = () => {
    if (activeBatch || queue.length === 0) return;
    scheduleDrain(queue.length >= batchSize ? 0 : flushIntervalMs, drain);
  };

  const publish = (events, attempt) => {
    activeBatch = { events, attempt };
    Promise.resolve()
      .then(() => publishBatch(events))
      .then(() => {
        activeBatch = null;
        scheduleNextBatch();
      })
      .catch(() => {
        if (attempt < maxRetries) {
          const delayMs = retryDelayMs * (2 ** attempt);
          scheduleDrain(delayMs, () => publish(events, attempt + 1));
          return;
        }
        activeBatch = null;
        scheduleNextBatch();
      });
  };

  function drain() {
    if (activeBatch || queue.length === 0) return;
    const events = Object.freeze(queue.splice(0, batchSize));
    publish(events, 0);
  }

  return Object.freeze({
    append(input) {
      let event;
      try {
        event = sanitizePublishedPilotEvent(input);
      } catch (_) {
        return INVALID_EVENT_RESULT;
      }

      // A queued timer cannot be trusted once the browser starts hiding or
      // unloading the page. The optional urgent publisher is injected by the
      // host (typically sendBeacon or fetch keepalive); this module invents no
      // endpoint and never retries the same event through both delivery paths.
      if (publishUrgent && shouldPublishUrgently()) {
        try {
          const accepted = publishUrgent(Object.freeze([event]));
          if (accepted === false) return URGENT_REJECTED_RESULT;
          if (accepted && typeof accepted.catch === "function") {
            accepted.catch(() => {});
          }
          return URGENT_PUBLISH_RESULT;
        } catch (_) {
          return URGENT_REJECTED_RESULT;
        }
      }

      if (bufferedCount() >= maxQueueSize) return QUEUE_FULL_RESULT;
      queue.push(event);
      scheduleNextBatch();
      return frozenResult("queued");
    },
  });
};
